import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import {
  personalList, personalCrear, personalEditar, personalEditarCuenta, personalDarAcceso, personalResetPass, personalQuitarAcceso,
  destacamentosList, cargosList, type Personal, type Destacamento, type Cargo,
} from '../api/seguridad';

const ROL_CUENTA: Record<string, string> = {
  agente_seguridad: 'Agente', radio_operador: 'Radio-Operador',
};

export default function MiPersonal() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();

  const [destId, setDestId] = useState<number | null>(user?.destacamento_id ?? null);
  const [destacamentos, setDestacamentos] = useState<Destacamento[]>([]);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [lista, setLista] = useState<Personal[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  // Modales
  const [modal, setModal] = useState<null | { tipo: 'crear' | 'editar' | 'acceso' | 'reset'; per?: Personal }>(null);

  // Si el usuario no tiene destacamento fijo (admin/superadmin), ofrecer selector.
  useEffect(() => {
    if (destId) return;
    destacamentosList().then(setDestacamentos).catch(() => {});
  }, [destId]);

  // Cargos/rangos para los selects de alta/edición.
  useEffect(() => { cargosList().then(setCargos).catch(() => {}); }, []);

  const cargar = useCallback(async () => {
    if (!destId) return;
    setCargando(true);
    try {
      setLista(await personalList(destId));
      setErr(null);
    } catch (e: any) {
      setErr(e?.message || 'Error al cargar el personal');
    } finally {
      setCargando(false);
    }
  }, [destId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function quitarAcceso(per: Personal) {
    if (!confirm(`¿Quitar el acceso a la app de ${per.nombre} ${per.apellido}? No podrá volver a ingresar hasta que le des acceso de nuevo.`)) return;
    try { await personalQuitarAcceso(per.id); await cargar(); }
    catch (e: any) { setErr(e?.message || 'No se pudo quitar el acceso'); }
  }

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={() => nav('/')} className="text-slate-500 hover:text-slate-900 text-xl leading-none">←</button>
        <div className="flex-1">
          <h1 className="text-slate-900 font-bold leading-tight">Mi personal</h1>
          <p className="text-slate-500 text-xs leading-tight">
            {user.destacamento_nombre ?? destacamentos.find((d) => d.id === destId)?.nombre ?? 'Comisaría'}
          </p>
        </div>
        {destId && (
          <button onClick={() => setModal({ tipo: 'crear' })}
            className="bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg px-3 py-2">
            + Agregar
          </button>
        )}
      </header>

      {err && <div className="bg-red-50 border-b border-red-200 text-red-700 text-xs px-4 py-1.5">{err}</div>}

      {/* Selector de destacamento (solo si no hay uno fijo) */}
      {!destId && (
        <div className="p-4 max-w-md">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Elegí un destacamento</label>
          <select onChange={(e) => setDestId(Number(e.target.value) || null)}
            className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
            <option value="">— seleccioná —</option>
            {destacamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </div>
      )}

      {/* Lista */}
      <main className="flex-1 overflow-y-auto p-3 max-w-3xl w-full mx-auto">
        {destId && cargando && lista.length === 0 && <p className="text-slate-400 text-sm text-center py-8">Cargando…</p>}
        {destId && !cargando && lista.length === 0 && (
          <p className="text-slate-400 text-sm text-center py-8">Todavía no cargaste personal. Tocá "+ Agregar".</p>
        )}

        <div className="space-y-2">
          {lista.map((per) => {
            const tieneCuenta = !!per.admin_usuario_id;
            const activa = per.cuenta_activa === 1;
            return (
              <div key={per.id} className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-900 font-semibold">{per.nombre} {per.apellido}</p>
                    <p className="text-slate-500 text-xs">
                      {per.cargo_nombre ?? 'Sin cargo'}
                      {per.dni && ` · DNI ${per.dni}`}
                      {per.telefono && ` · ${per.telefono}`}
                    </p>
                    {tieneCuenta ? (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">✓ Acceso app</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{ROL_CUENTA[per.cuenta_rol ?? ''] ?? per.cuenta_rol}</span>
                        {per.tiene_push ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">📲 push</span> : null}
                        <span className="text-slate-500 text-[11px]">{per.cuenta_email}</span>
                      </div>
                    ) : (
                      <span className="mt-1.5 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Sin acceso a la app</span>
                    )}
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <button onClick={() => setModal({ tipo: 'editar', per })}
                    className="border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg py-2 px-3 text-sm font-semibold">
                    ✏️ Editar
                  </button>
                  {!tieneCuenta ? (
                    <button onClick={() => setModal({ tipo: 'acceso', per })}
                      className="flex-1 bg-brand-500 hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-semibold">
                      Dar acceso a la app
                    </button>
                  ) : (
                    <>
                      <button onClick={() => setModal({ tipo: 'reset', per })}
                        className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg py-2 text-sm font-semibold">
                        🔑 Resetear pass
                      </button>
                      <button onClick={() => quitarAcceso(per)}
                        className="flex-1 border border-red-300 hover:bg-red-50 text-red-700 rounded-lg py-2 text-sm font-semibold">
                        Quitar acceso
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {modal && (
        <ModalPersonal
          modal={modal}
          destId={destId!}
          cargos={cargos}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await cargar(); }}
          onError={setErr}
        />
      )}
    </div>
  );
}

// ─── Modal (crear roster / dar acceso / resetear pass) ──────────────────
function ModalPersonal({
  modal, destId, cargos, onClose, onDone, onError,
}: {
  modal: { tipo: 'crear' | 'editar' | 'acceso' | 'reset'; per?: Personal };
  destId: number;
  cargos: Cargo[];
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [f, setF] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = { rol: 'agente_seguridad' };
    if (modal.tipo === 'editar' && modal.per) {
      base.nombre = modal.per.nombre;
      base.apellido = modal.per.apellido;
      base.dni = modal.per.dni ?? '';
      base.telefono = modal.per.telefono ?? '';
      base.cargo_id = modal.per.cargo_id != null ? String(modal.per.cargo_id) : '';
      if (modal.per.admin_usuario_id) {
        base.email = modal.per.cuenta_email ?? '';
        base.rol = modal.per.cuenta_rol ?? 'agente_seguridad';
      }
    }
    return base;
  });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const titulo = modal.tipo === 'crear' ? 'Agregar al personal'
    : modal.tipo === 'editar' ? `Editar ${modal.per?.nombre} ${modal.per?.apellido}`
    : modal.tipo === 'acceso' ? `Dar acceso a ${modal.per?.nombre}`
    : `Resetear contraseña de ${modal.per?.nombre}`;

  async function guardar() {
    setBusy(true);
    try {
      const cargoId = f.cargo_id ? Number(f.cargo_id) : undefined;
      if (modal.tipo === 'crear') {
        if (!f.nombre?.trim() || !f.apellido?.trim()) throw new Error('Nombre y apellido son obligatorios');
        await personalCrear(destId, { nombre: f.nombre.trim(), apellido: f.apellido.trim(), dni: f.dni, telefono: f.telefono, cargo_id: cargoId });
      } else if (modal.tipo === 'editar') {
        if (!f.nombre?.trim() || !f.apellido?.trim()) throw new Error('Nombre y apellido son obligatorios');
        await personalEditar(modal.per!.id, { nombre: f.nombre.trim(), apellido: f.apellido.trim(), dni: f.dni, telefono: f.telefono, cargo_id: cargoId });
        // Si tiene cuenta de app, actualizar también email + rol de la cuenta.
        if (modal.per!.admin_usuario_id) {
          await personalEditarCuenta(modal.per!.id, { email: f.email?.trim(), rol: f.rol });
        }
      } else if (modal.tipo === 'acceso') {
        await personalDarAcceso(modal.per!.id, { email: f.email ?? '', password: f.password ?? '', rol: (f.rol as any) || 'agente_seguridad' });
      } else {
        await personalResetPass(modal.per!.id, f.password ?? '');
      }
      onDone();
    } catch (e: any) {
      onError(e?.message || 'Error');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1300] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}>
        <h2 className="text-slate-900 font-bold text-lg mb-3">{titulo}</h2>
        <div className="space-y-3">
          {(modal.tipo === 'crear' || modal.tipo === 'editar') && (
            <>
              <Campo label="Nombre" defaultValue={f.nombre} onChange={(v) => set('nombre', v)} />
              <Campo label="Apellido" defaultValue={f.apellido} onChange={(v) => set('apellido', v)} />
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Cargo / rango</label>
                <select value={f.cargo_id ?? ''} onChange={(e) => set('cargo_id', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
                  <option value="">— sin cargo —</option>
                  {cargos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <Campo label="DNI (opcional)" defaultValue={f.dni} onChange={(v) => set('dni', v)} />
              <Campo label="Teléfono (opcional)" defaultValue={f.telefono} onChange={(v) => set('telefono', v)} />
              {modal.tipo === 'editar' && modal.per?.admin_usuario_id && (
                <div className="pt-3 mt-1 border-t border-slate-200 space-y-3">
                  <p className="text-xs font-semibold text-slate-500">Cuenta de app</p>
                  <Campo label="Email (usuario)" type="email" defaultValue={f.email} onChange={(v) => set('email', v)} />
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Función en la app</label>
                    <select value={f.rol} onChange={(e) => set('rol', e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
                      <option value="agente_seguridad">Agente (responde en la calle)</option>
                      <option value="radio_operador">Radio-Operador (opera en la central)</option>
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">Es la función en el sistema, no el rango. El rango (Cabo, Oficial…) es el <b>Cargo</b> de arriba.</p>
                  </div>
                  <p className="text-[11px] text-slate-400">La contraseña se cambia con "🔑 Resetear pass".</p>
                </div>
              )}
            </>
          )}
          {modal.tipo === 'acceso' && (
            <>
              {modal.per?.cargo_nombre && (
                <p className="text-[11px] text-slate-500 -mt-1">Rango: <b className="text-slate-700">{modal.per.cargo_nombre}</b></p>
              )}
              <Campo label="Email (usuario)" type="email" onChange={(v) => set('email', v)} />
              <Campo label="Contraseña" type="password" onChange={(v) => set('password', v)} />
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Función en la app</label>
                <select value={f.rol} onChange={(e) => set('rol', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
                  <option value="agente_seguridad">Agente (responde en la calle)</option>
                  <option value="radio_operador">Radio-Operador (opera en la central)</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Es la función en el sistema, no el rango. El rango se define con el <b>Cargo</b> del roster.</p>
              </div>
            </>
          )}
          {modal.tipo === 'reset' && (
            <Campo label="Nueva contraseña" type="password" onChange={(v) => set('password', v)} />
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl py-2.5 text-sm font-semibold">Cancelar</button>
          <button disabled={busy} onClick={guardar} className="flex-1 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, type = 'text', defaultValue, onChange }: { label: string; type?: string; defaultValue?: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input type={type} autoComplete="off" defaultValue={defaultValue} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand-500" />
    </div>
  );
}
